import { useEffect, useCallback, useRef } from 'react';
import { useFullscreen } from './useFullscreen';
import {
  getFullscreenElement,
  requestFullscreen,
  exitFullscreen,
} from '../utils/fullscreenApi';

/**
 * Presenter shell fullscreen + redirect iframe-only fullscreen to the shell.
 */
export function usePresenterFullscreen({
  modalShellRef,
  iframeRef,
  isPresenterOpen,
  commentsOpen,
  isPinned,
  pinPanel,
}) {
  const { isFullscreen, toggleFullscreen, enterFullscreen } = useFullscreen(modalShellRef);
  const promotingRef = useRef(false);

  const prepareCommentsForFullscreen = useCallback(() => {
    if (commentsOpen && !isPinned) {
      pinPanel();
    }
  }, [commentsOpen, isPinned, pinPanel]);

  const handleToggleFullscreen = useCallback(async () => {
    if (!isFullscreen) {
      prepareCommentsForFullscreen();
    }
    await toggleFullscreen();
  }, [isFullscreen, prepareCommentsForFullscreen, toggleFullscreen]);

  const promoteIframeFullscreenToShell = useCallback(async () => {
    const shell = modalShellRef?.current;
    const iframe = iframeRef?.current;
    if (!shell || !iframe || promotingRef.current) return;

    const fsEl = getFullscreenElement();
    if (fsEl !== iframe) return;

    promotingRef.current = true;
    try {
      await exitFullscreen();
      prepareCommentsForFullscreen();
      await requestFullscreen(shell);
    } finally {
      promotingRef.current = false;
    }
  }, [modalShellRef, iframeRef, prepareCommentsForFullscreen]);

  useEffect(() => {
    if (!isPresenterOpen) return undefined;

    const onFullscreenChange = () => {
      promoteIframeFullscreenToShell();
    };

    const events = ['fullscreenchange', 'webkitfullscreenchange', 'mozfullscreenchange', 'MSFullscreenChange'];
    events.forEach((name) => document.addEventListener(name, onFullscreenChange));
    return () => events.forEach((name) => document.removeEventListener(name, onFullscreenChange));
  }, [isPresenterOpen, promoteIframeFullscreenToShell]);

  return {
    isFullscreen,
    toggleFullscreen: handleToggleFullscreen,
    enterFullscreen,
  };
}
